import { useEffect, useRef, useState } from 'react';
import {
  eingebauteKennung,
  frageAusgelieferteKennung,
  istAndereVersion,
} from '../lib/neueVersion';

/** Wie oft ein sichtbarer Tab nachfragt; dazu kommt jede Rückkehr ins Fenster. */
export const PRUEF_ABSTAND_MS = 5 * 60 * 1000;

interface Optionen {
  /** Nur im ausgelieferten Build — der lokale Dev-Server hat keine version.json. */
  aktiv?: boolean;
  abstandMs?: number;
  abrufen?: typeof fetch;
}

/**
 * Meldet, sobald der Server einen anderen Build ausliefert als den, der in diesem
 * Tab läuft. Gefragt wird beim Start, in Abständen (nur bei sichtbarem Tab) und
 * immer dann, wenn das Fenster wieder in den Blick kommt. Ist der Unterschied einmal
 * erkannt, bleibt es dabei und die Abfragen hören auf — bis zum Neuladen ändert sich
 * am Bundle nichts mehr.
 */
export function useNeueVersion({
  aktiv = import.meta.env.PROD,
  abstandMs = PRUEF_ABSTAND_MS,
  abrufen,
}: Optionen = {}): boolean {
  const [neu, setNeu] = useState(false);
  const abrufenRef = useRef(abrufen);
  abrufenRef.current = abrufen;

  useEffect(() => {
    if (!aktiv || neu) return;
    const eingebaut = eingebauteKennung();
    let beendet = false;
    // Fokus und Sichtbarkeit feuern beim Zurückkehren meist gleichzeitig.
    let laeuft = false;

    const pruefe = () => {
      if (beendet || laeuft) return;
      laeuft = true;
      void frageAusgelieferteKennung(abrufenRef.current).then((ausgeliefert) => {
        laeuft = false;
        if (!beendet && istAndereVersion(eingebaut, ausgeliefert)) setNeu(true);
      });
    };
    const wennSichtbar = () => {
      if (document.visibilityState === 'visible') pruefe();
    };

    pruefe();
    const timer = window.setInterval(wennSichtbar, abstandMs);
    window.addEventListener('focus', pruefe);
    document.addEventListener('visibilitychange', wennSichtbar);
    return () => {
      beendet = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', pruefe);
      document.removeEventListener('visibilitychange', wennSichtbar);
    };
  }, [aktiv, neu, abstandMs]);

  return neu;
}
